import type {
  InventoryPurchaseOrder,
  InventoryPurchaseOrderItem,
} from '@/services/inventoryService';

/**
 * Per-item PO price history (backlog item #8 — price-change tracking).
 *
 * Every PO line stores a `unit_cost_snapshot` at creation time (falling back
 * to `unit_price` for older rows that pre-date the snapshot column). This
 * module folds those snapshots into a newest-first history per catalog item,
 * entirely client-side from the workflow snapshot — no extra queries.
 *
 * All comparisons and deltas are done in integer cents (mirroring the PO
 * print view) so "changed by S$0.00" rounding artefacts can never appear.
 */

export interface PriceHistoryEntry {
  purchaseOrderId: string;
  poNumber: string;
  supplierId: string;
  /** PO creation date (ISO string) — when this price was recorded. */
  recordedAt: string;
  unitCostCents: number;
}

export const toCents = (value: number): number => Math.round(value * 100);

export const formatCentsSGD = (cents: number): string =>
  `S$${(Math.abs(cents) / 100).toLocaleString('en-SG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/**
 * Builds a newest-first price history per inventory item id. Cancelled POs
 * are excluded; lines without a usable cost (null, zero, or negative) are
 * skipped. Draft POs are included — a draft price is still the most recent
 * signal the clinic has recorded for an item.
 */
export function buildPriceHistoryByItemId(
  purchaseOrders: InventoryPurchaseOrder[],
  purchaseOrderItems: InventoryPurchaseOrderItem[]
): Map<string, PriceHistoryEntry[]> {
  const poById = new Map(purchaseOrders.map((po) => [po.id, po]));
  const history = new Map<string, PriceHistoryEntry[]>();

  purchaseOrderItems.forEach((line) => {
    if (!line.inventory_item_id) {
      return;
    }

    const po = poById.get(line.purchase_order_id);
    if (!po || po.status === 'cancelled') {
      return;
    }

    const unitCost = line.unit_cost_snapshot ?? line.unit_price;
    if (unitCost == null || unitCost <= 0) {
      return;
    }

    const entries = history.get(line.inventory_item_id) ?? [];
    entries.push({
      purchaseOrderId: po.id,
      poNumber: po.po_number,
      supplierId: po.supplier_id,
      recordedAt: po.created_at,
      unitCostCents: toCents(unitCost),
    });
    history.set(line.inventory_item_id, entries);
  });

  history.forEach((entries) => {
    entries.sort((left, right) => right.recordedAt.localeCompare(left.recordedAt));
  });

  return history;
}
